import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { getFileNameFromUrl, getFileTypeCategory, getObjectUrlFromBase64 } from "../../utils/fileHelper";
import { fetchFileBytesData } from "../../store/apiActions";
import wordIcon from "../../assets/icons/word.svg";
import pdfIcon from "../../assets/icons/pdf.svg";
import excelIcon from "../../assets/icons/microsoft-excel.svg";
import pptIcon from "../../assets/icons/microsoft-powerpoint.svg";
import textIcon from "../../assets/icons/txt.svg";
import xmlIcon from "../../assets/icons/xml.svg";
import htmlIcon from "../../assets/icons/html.svg";
import jsonIcon from "../../assets/icons/json.svg";
import csvIcon from "../../assets/icons/csv.svg";
import imageIcon from "../../assets/icons/image.svg";
import "./MediaSection.css";

const fileTypeIcons = {
    pdf: pdfIcon,
    word: wordIcon,
    excel: excelIcon,
    ppt: pptIcon,
    text: textIcon,
    html: htmlIcon,
    xml: xmlIcon,
    json: jsonIcon,
    csv: csvIcon,
    image: imageIcon,
};

const MediaFileItem = ({ file, handleMediaClick, pair }) => {
    const dispatch = useDispatch();
    const [imageSrc, setImageSrc] = useState(null);
    const [isLoadingImage, setIsLoadingImage] = useState(false);
    const [imageLoadFailed, setImageLoadFailed] = useState(false);

    const fileName = file?.filename || getFileNameFromUrl(file?.path);
    const fileType = getFileTypeCategory(file);
    const fileIcon = fileTypeIcons[fileType] || wordIcon;
    const isImage = fileType === 'image';

    useEffect(() => {
        const loadImage = async () => {
            // Reset states
            setImageLoadFailed(false);

            // If we already have base64 or path, use it
            if (file?.base64_image) {
                const ext = fileName.split('.').pop().toLowerCase();
                setImageSrc(getObjectUrlFromBase64(file.base64_image, ext));
                return;
            }

            if (file?.path && file.path.startsWith('blob:')) {
                setImageSrc(file.path);
                return;
            }

            if (file?.decrypted_base64) {
                const ext = fileName.split('.').pop().toLowerCase();
                setImageSrc(getObjectUrlFromBase64(file.decrypted_base64, ext));
                return;
            }

            // For images without base64 data, fetch from API
            if (isImage && file?.file_id && !file?.base64_image && !file?.decrypted_base64) {
                setIsLoadingImage(true);
                try {
                    const response = await dispatch(fetchFileBytesData({ file_id: file.file_id }));
                    const { file_data } = response;
                    if (file_data) {
                        const ext = fileName.split('.').pop().toLowerCase();
                        const objectUrl = getObjectUrlFromBase64(file_data, ext);
                        setImageSrc(objectUrl);
                    } else {
                        setImageLoadFailed(true);
                    }
                } catch (error) {
                    console.error('Error fetching image data:', error);
                    setImageLoadFailed(true);
                } finally {
                    setIsLoadingImage(false);
                }
            }
        };

        if (isImage) {
            loadImage();
        }
    }, [file, fileName, isImage, dispatch]);

    // Determine what to show
    const showImagePreview = isImage && imageSrc && !imageLoadFailed;
    const showLoading = isImage && isLoadingImage;

    return (
        <button
            className={`media-button button-style ${showImagePreview ? 'image-only' : ''}`}
            type="button"
            onClick={() => handleMediaClick && handleMediaClick(file, pair)}
        >
            <div className={`media-icon ${showImagePreview ? 'image-preview-container' : ''}`}>
                {showLoading ? (
                    <div className="media-image-loader">
                        <div className="spinner-small"></div>
                    </div>
                ) : showImagePreview ? (
                    <img src={imageSrc} alt={fileName} className="media-image-preview" />
                ) : (
                    <img src={fileIcon} alt={`${fileType}-icon`} className="media-icon-img" />
                )}
            </div>
            {!showImagePreview && (
                <div className="media-file-name" title={fileName}>
                    {fileName}
                </div>
            )}
        </button>
    );
};

const MediaSection = ({ pair, handleMediaClick }) => {
    if (!pair?.file_paths?.length) return null;

    return (
        <div className="media-section">
            {pair.file_paths.map((file, fileIndex) => (
                <MediaFileItem
                    key={fileIndex}
                    file={file}
                    handleMediaClick={handleMediaClick}
                    pair={pair}
                />
            ))}
        </div>
    );
};

export default MediaSection;
